import React, { useState, useEffect, useContext, useRef } from 'react';
import { ThemeContext } from '../contexts/ThemeContext';
import { AuthContext } from '../contexts/AuthContext';
import { ResultsContext } from '../contexts/ResultsContext';
import SearchButton from './SearchButton';
import './UsernamePicker.css';

/**
 * UsernamePicker component provides a datalist input for selecting usernames
 * from the tournament data
 * 
 * @param {Object} props - Component props
 * @param {string} props.value - Current username value
 * @param {Function} props.onChange - Callback function when username changes
 * @param {string} props.tournamentId - Tournament ID to fetch usernames from
 * @param {string} props.placeholder - Placeholder text for the input
 * @param {Function} props.onKeyPress - Callback function for key press events
 * @param {Function} props.onSearch - Callback function when search button is clicked
 * @param {boolean} props.showSearchButton - Whether to show the search button
 * @param {boolean} props.isSearching - Whether a search is in progress
 */
function UsernamePicker({ 
  value = '', 
  onChange, 
  tournamentId = '', 
  placeholder = 'Enter player name',
  onKeyPress,
  onSearch,
  showSearchButton = false,
  isSearching = false
}) {
  const [usernames, setUsernames] = useState([]);
  const [loading, setLoading] = useState(false);
  const { theme } = useContext(ThemeContext);
  const { isAuthenticated, user, saveUsernameToServer } = useContext(AuthContext);
  const { results } = useContext(ResultsContext);
  const previousValueRef = useRef(value);

  // Extract usernames from results data when it changes
  useEffect(() => {
    if (!results || !results.games) {
      setUsernames([]);
      return;
    }

    setLoading(true);

    try {
      // Extract unique usernames from all games
      const uniqueUsernames = new Set();
      
      results.games.forEach(game => {
        if (game.leaderboard && Array.isArray(game.leaderboard)) {
          game.leaderboard.forEach(entry => {
            if (entry.userName) {
              uniqueUsernames.add(entry.userName);
            }
          });
        }
      });
      
      // Convert Set to Array and sort alphabetically
      const usernamesArray = Array.from(uniqueUsernames).sort((a, b) => 
        a.toLowerCase().localeCompare(b.toLowerCase())
      );
      
      setUsernames(usernamesArray);
    } catch (error) {
      console.error('Error processing usernames:', error);
    } finally {
      setLoading(false);
    }
  }, [results]);

  // Check if the current value matches any username in the datalist (case insensitive)
  useEffect(() => {
    // Skip if there's no search function, no value, or we're already searching
    if (!onSearch || !value || isSearching || value === previousValueRef.current) {
      previousValueRef.current = value;
      return;
    }

    // Check if the current value exactly matches a username in the datalist (case insensitive)
    const matchedUsername = usernames.find(
      username => username.toLowerCase() === value.toLowerCase()
    );

    if (matchedUsername) {
      // If there's a match, update the input value to the exact case from the datalist
      if (onChange && matchedUsername !== value) {
        onChange(matchedUsername);
      }
      
      // Trigger the search
      onSearch();
    }

    previousValueRef.current = value;
  }, [value, usernames, onSearch, onChange, isSearching]);

  // Handle input change
  const handleChange = (e) => {
    if (onChange) {
      onChange(e.target.value);
    }
  };

  // Handle search button click
  const handleSearchClick = () => {
    if (onSearch) {
      // Save the username to localStorage
      localStorage.setItem('lastUsername', value);
      
      // If user is authenticated, save the username to the server
      if (isAuthenticated && value) {
        saveUsernameToServer(value);
      }
      
      onSearch();
    }
  };

  // Handle input selection from datalist
  const handleInput = (e) => {
    // This event fires when a datalist option is selected
    if (onSearch && usernames.includes(e.target.value)) {
      // Save the username to localStorage
      localStorage.setItem('lastUsername', e.target.value);
      
      // If user is authenticated, save the username to the server
      if (isAuthenticated && e.target.value) {
        saveUsernameToServer(e.target.value);
      }
      
      onSearch();
    }
  };

  // Handle click on input to select all text
  const handleClick = (e) => {
    e.target.select();
  };

  // Load saved username from user object or localStorage on component mount
  useEffect(() => {
    if (isAuthenticated && user?.selectedUsername && !value) {
      // If user is authenticated and has a saved username, use it
      onChange(user.selectedUsername);
    } else if (!value) {
      // Otherwise, try to load from localStorage
      const savedUsername = localStorage.getItem('lastUsername');
      if (savedUsername) {
        onChange(savedUsername);
      }
    }
  }, [isAuthenticated, user, onChange, value]);

  return (
    <div className={`username-picker-container ${showSearchButton ? 'with-search-button' : ''}`}>
      <input
        type="text"
        id="player-username-search"
        name="player-username-search"
        list="usernames-list"
        value={value}
        onChange={handleChange}
        onInput={handleInput}
        onClick={handleClick}
        onKeyPress={(e) => {
          if (e.key === 'Enter') {
            // Save the username to localStorage
            localStorage.setItem('lastUsername', value);
            
            // If user is authenticated, save the username to the server
            if (isAuthenticated && value) {
              saveUsernameToServer(value);
            }
          }
          if (onKeyPress) onKeyPress(e);
        }}
        placeholder={loading ? 'Loading usernames...' : placeholder}
        className={`username-picker-input ${theme} ${showSearchButton ? 'with-search-button' : ''}`}
        autoComplete="off"
        data-lpignore="true"
      />
      {showSearchButton && (
        <SearchButton 
          onClick={handleSearchClick} 
          disabled={isSearching}
        />
      )}
      <datalist id="usernames-list">
        {usernames.map(username => (
          <option key={username} value={username} />
        ))}
      </datalist>
    </div>
  );
}

export default UsernamePicker;